import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SharedAccountNotice from "../SharedAccountNotice";
import RouteFlowMap from "./RouteFlowMap";
import SelectionPanel, { NavSection } from "../../common/configPanel/SelectionPanel";
import SelectInput from "../../common/SelectInput";
import ActionSectionForm from "../../common/configPanel/ActionSectionForm";
import { useConfigEditor } from "../../common/configPanel/useConfigEditor";
import {
    actionCanCommitForm, slugOf,
} from "../../common/configPanel/actionSetup";
import { isActionVisible } from "../../common/SchemaFormTemplates/checkSchema";
import { ADVANCED_CAT, buildActionNav } from "../../common/configPanel/buildActionNav";
import { AccountBundle, FlowDirectionKey, Selection } from "./flowMapTypes";
import { buildFlowArrows, buildGraph, hasType, useAccountBundles } from "./useFlowGraph";
import { useTranslation } from "react-i18next";
import { getRouteKind } from "../../common/routeKind";
import RouteDetailsWebhook from "../RouteDetailsWebhook";
import { getDestinationAccount } from "../../destinations/DestinationHelperFunctions";
import { getReverseRoutes, getRouteApiKey, updateConnection } from "../../../api/routes/routesApi";
import UnsavedSectionPrompt, { SectionPrompt } from "../../common/configPanel/UnsavedSectionPrompt";
import HowToConnect from "../../common/configPanel/HowToConnect";
import { computeNeedsSetup } from "./needsSetup";
import { baseUrlPolicy } from "./baseUrlPolicy";
import { validateBaseUrl } from "../../destinations/validateBaseUrl";
import EarthRangerSiteInput from "../../destinations/EarthRangerSiteInput";
import { constructEarthRangerUrl, extractEarthRangerSiteName, isEarthRangerDestinationType } from "../../destinations/earthRangerUrl";
import { buttonClasses } from "../../common/tailwindClassnames";
import ModalShell from "../../common/ModalShell";
import { HoverTooltip } from "../../common/HoverTooltip";
// The portal's canonical switch (headless-ui + the shared switchContainer classes),
// reused here so the direction toggle looks like every other toggle in the app.
import CustomCheckbox from "../../common/SchemaFormTemplates/CustomCheckbox";
import { ActionPillButton } from "../flowIcons";
import { Rule } from "../transformationRules/fieldMappingTypes";
import { routeRecordId } from "../routeDisplayName";

export function useSelectionFromUrl(routeId: string): {
    selection: Selection;
    select: (next: Selection) => void;
} {
    const navigate = useNavigate();
    // One splat route carries every selection depth:
    //   account/<integrationId>[/<section>]  |  flow/<destinationId>/<in|out>[/<section>]
    const splat = useParams()["*"] || "";
    const [kind, id, third, fourth] = splat.split("/").filter(Boolean);
    const base = `/routes/${routeId}/configurations`;

    let selection: Selection = { kind: "none" };
    if (kind === "account" && id) {
        selection = { kind: "account", integrationId: id, section: third };
    } else if (kind === "flow" && id && (third === "in" || third === "out")) {
        selection = { kind: "flow", destinationId: id, direction: third as FlowDirectionKey, section: fourth };
    }

    const select = (next: Selection) => {
        if (next.kind === "none") return navigate(base);
        if (next.kind === "account")
            return navigate(`${base}/account/${next.integrationId}${next.section ? "/" + next.section : ""}`);
        return navigate(`${base}/flow/${next.destinationId}/${next.direction}${next.section ? "/" + next.section : ""}`);
    };

    return { selection, select };
}



const ConfigurationsTab = (props: any) => {
    const { route, routeProvider, token, currentMemberIsAdmin } = props;
    const { t } = useTranslation();
    const providerId: string = routeProvider?.id || route?.provider?.id;
    const destinations: any[] = React.useMemo(() => route?.destinations || [], [route?.destinations]);
    const accountIds = React.useMemo(
        () => [providerId, ...destinations.map((d: any) => d.id)].filter(Boolean),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [providerId, destinations.map((d: any) => d.id).join(",")]);

    // The parent fetches the provider integration itself — seed the bundle map
    // with it instead of re-fetching the same record.
    const { bundles, loading, failed, reload } = useAccountBundles(token, accountIds, providerId, routeProvider);
    // Selection URLs must extend the id the page is ALREADY addressed by (the
    // parent's :route_id), never a re-derived one: deriving it flipped between
    // the route-record and provider-integration id spaces for records reached
    // through routing_rules, so a node click rewrote the URL to a different
    // spelling of the same page.
    const routeUrlId: string = props.routeUrlId || route?.default_route?.id || route?.id;
    const { selection, select } = useSelectionFromUrl(routeUrlId);

    const selectedAccountId = selection.kind === "account" ? selection.integrationId : undefined;
    const selectedArrowId = selection.kind === "flow" ? `${selection.destinationId}-${selection.direction}` : undefined;

    // Structural graph: every input is either a primitive or identity-stable
    // parent state, so a plain dependency array works. Selection is layered on
    // afterwards so clicking around never rebuilds nodes and edges.
    const routeEnabled = routeProvider?.enabled !== false;
    const structural = React.useMemo(() => {
        const built = buildFlowArrows({
            providerId,
            provider: route?.provider,
            destinations,
            reverseRoutesBySite: props.reverseRoutesBySite || {},
            providerCanReceive: !!props.providerCanReceive,
            routeEnabled,
            // undefined while the destination's integration hasn't loaded — the
            // arrow is drawn unavailable but claims no reason for it.
            destinationCanSend: (destId: string) => bundles[destId] ? hasType(bundles[destId].actions, "pull") : undefined,
            t,
        });

        // Who still has work to do — one shared answer for the node badges and
        // the Quick Start steps. All badges live on nodes in this model.
        const setup = computeNeedsSetup({
            providerId, routeProvider, bundles, destinations, arrows: built,
        });
        const { needsSetupAccounts } = setup;

        const graph = buildGraph({
            provider: route?.provider,
            destinations,
            arrows: built,
            bundles,
            destinationsWithRules: props.destinationsWithRules || new Set<string>(),
            needsSetupAccounts,
            t,
        });
        return { ...graph, ...setup, arrows: built };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId, route?.provider, routeProvider, destinations, bundles, routeEnabled,
        props.reverseRoutesBySite, props.providerCanReceive, props.destinationsWithRules, t]);

    const arrows = structural.arrows;

    // The Quick Start banner lives on the page above this tab and has no view of
    // the per-account configurations, so report what is still unconfigured.
    const { onNeedsSetupChange } = props;
    const needsSetupCount = loading ? null : structural.needsSetupCount;
    const providerNeedsSetup = structural.providerNeedsSetup;
    const destinationsNeedSetup = structural.destinationsNeedSetup;
    // Nothing loaded at all (no token yet, or it expired) is not a report: with an
    // empty bundle map every account would read as unconfigured and the banner
    // would flip a finished Route to all-warnings.
    const anyBundleLoaded = Object.keys(bundles).length > 0;
    useEffect(() => {
        if (needsSetupCount !== null && anyBundleLoaded) onNeedsSetupChange?.({
            total: needsSetupCount, provider: providerNeedsSetup, destinations: destinationsNeedSetup,
        });
    }, [needsSetupCount, providerNeedsSetup, destinationsNeedSetup, anyBundleLoaded, onNeedsSetupChange]);

    const { nodes, edges } = React.useMemo(() => ({
        nodes: structural.nodes.map((n: any) =>
            n.id === selectedAccountId ? { ...n, data: { ...n.data, isSelected: true } } : n),
        edges: structural.edges.map((e: any) =>
            e.id === selectedArrowId ? { ...e, data: { ...e.data, isSelected: true } } : e),
    }), [structural, selectedAccountId, selectedArrowId]);

    // Confirmations render through the shared ModalShell rather than window.confirm,
    // so they match every other dialog in the portal.
    type Confirmation = {
        title: string; body: string; confirmLabel: string; destructive?: boolean; onConfirm: () => void;
    };
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
    // The Save-or-discard question, shared with every configuration screen.
    const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(null);
    const navigate = useNavigate();

    // A map with a single node has nothing else to click — open its panel instead
    // of asking the user to click the only thing there is. Once per mount only:
    // deselecting is also a deliberate act (removing the last destination ends
    // with select({kind:"none"})), and re-firing dragged the user back into the
    // form they had just left.
    const autoSelectSettled = useRef(false);
    useEffect(() => {
        // Anything other than "an empty map with nothing selected" means the user
        // has somewhere to be: a selection of their own, or destinations to click.
        // Latching here is what keeps a later deselect (removing the last
        // destination ends with select({kind:"none"})) from being overridden.
        if (selection.kind !== "none" || destinations.length > 0) {
            autoSelectSettled.current = true;
            return;
        }
        if (autoSelectSettled.current || !providerId || !routeUrlId) return;
        autoSelectSettled.current = true;
        navigate(`/routes/${routeUrlId}/configurations/account/${providerId}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, providerId, destinations.length, routeUrlId]);

    // ---- unsaved edits, stashed per account and PATCHed on Save -------------
    // Shared with every other configuration screen (destination page, wizard) so
    // "Save" always means "PATCH this one section".
    const editor = useConfigEditor({ token, t, onSaved: (id: string) => reload(id) });
    const { dirtyIds, anyDirty } = editor;

    // Switching to a different node/arrow throws away whatever is stashed for the
    // current one, so ask first. Switching sections inside the same selection keeps
    // the same pending stash and never prompts.
    const guardedSelect = (next: Selection) => {
        const sameAccount = next.kind === "account" && selection.kind === "account" && next.integrationId === selection.integrationId;
        const sameFlow = next.kind === "flow" && selection.kind === "flow"
            && next.destinationId === selection.destinationId && next.direction === selection.direction;
        // The General draft is unsaved state too, even though it is not an
        // action configuration. The same Save-or-discard question a section
        // switch asks — offering only "discard" made the user cancel, save by
        // hand, and come back to click where they wanted to go.
        if ((anyDirty || generalDraft !== null) && !sameAccount && !sameFlow) {
            return setSectionPrompt({
                onSave: savePendingEdits,
                onDiscard: discardPendingEdits,
                proceed: () => select(next),
            });
        }
        select(next);
    };

    // Leaving the Route entirely deserves the same prompt as moving between
    // selections — unsaved edits would just vanish when the panel unmounts.
    const guardedNavigate = (to: string) => {
        if (anyDirty || generalDraft !== null) {
            return setSectionPrompt({
                onSave: savePendingEdits,
                onDiscard: discardPendingEdits,
                proceed: () => navigate(to),
            });
        }
        navigate(to);
    };

    // ---- other Routes sharing a destination account -------------------
    // Fetched lazily for the destination whose panel is open (the notice only
    // renders there), and cached per id — never one call per destination upfront.
    const [sharedBySite, setSharedBySite] = useState<Record<string, { id: string, name: string }[]>>({});
    const sharedFetched = useRef<Set<string>>(new Set());
    const selectedDestId = selection.kind === "account" && selection.integrationId !== providerId
        ? selection.integrationId : undefined;
    useEffect(() => {
        if (!token || !selectedDestId || sharedFetched.current.has(selectedDestId)) return;
        sharedFetched.current.add(selectedDestId);
        // Same reasoning as the api-key fetch: keyed by id, so a late response is
        // safe to store. Dropping it while the id stayed latched meant the
        // shared-account warning never appeared for that destination again — the
        // UI would then affirm that nothing else is affected.
        (async () => {
            try {
                const data = await getReverseRoutes(token, selectedDestId);
                const others = (data?.results || data || [])
                    .filter((c: any) => c?.id && c.id !== route?.id)
                    // No name fallback to the raw id: a Route the user can't
                    // resolve renders as a masked count, never as an identifier.
                    // Route URLs address the route record, not the connection.
                    .map((c: any) => ({ id: routeRecordId(c), name: c.provider?.name }))
                    .filter((c: any) => c.id);
                setSharedBySite((prev) => ({ ...prev, [selectedDestId]: others }));
            } catch (e) {
                // Allow a retry on the next visit to this panel.
                sharedFetched.current.delete(selectedDestId);
                console.error("failed to load routes sharing site", selectedDestId, e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedDestId, route?.id]);

    // ---- section content ---------------------------------------------------
    const renderActionForm = (bundle: AccountBundle, action: any, editable: boolean) => (
        <ActionSectionForm
            target={bundle}
            action={action}
            editable={editable}
            userIsAdmin={currentMemberIsAdmin}
            token={token}
            formVersion={editor.formVersion[bundle.id] || 0}
            errors={editor.formErrors[bundle.id]?.[action.id]}
            formData={editor.formDataFor(bundle, action)}
            onChange={(data: any, fieldId?: string) => editor.snapshotChange(bundle, action.id, data, fieldId)}
            test={props.onTestAction
                ? { onTestAction: props.onTestAction, loading: props.testActionLoading, response: props.testActionResponse }
                : undefined}
        />
    );

    // Field mapping lives on the Route (per flow), not on the account, and is
    // edited in the existing transformation-rules side panel.
    const fieldMappingSection = (dest: any) => {
        const rule: Rule | null = dest ? props.getRule(dest.id) : null;
        if (!rule) return (
            <>
                <p className="text-sm text-secondary-text text-center max-w-xl mx-auto mt-5">
                    {t("Rules change the data on its way to this destination")}
                </p>
                <ActionPillButton label={t("Add Rules")} onClick={() => props.onOpenRules(dest)} />
            </>
        );
        const mapEntries = rule.mapping.map ? Object.entries(rule.mapping.map) : [];
        return (
            <>
                <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-semibold text-off-black">{t("Field mapping")}</span>
                    <button className={`ml-auto ${buttonClasses.inlineLink}`}
                        onClick={() => props.onOpenRules(dest)}>
                        {t("Edit")} ▸
                    </button>
                </div>
                <div className="rounded border border-gray-lines bg-secondary-background px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="bg-light-green text-route-green rounded px-2 py-0.5 text-xs font-semibold">
                            {rule.streamType === "ev" ? t("Events") : t("Observations")}
                        </span>
                        <span className="font-mono text-xs text-off-black">
                            {rule.mapping.provider_field || t("(source value)")} → {rule.mapping.destination_field}
                        </span>
                    </div>
                    {mapEntries.length > 0 && (
                        <div className="flex flex-col gap-1">
                            {mapEntries.slice(0, 4).map(([from, to]) => (
                                <div key={from} className="flex items-center gap-2 font-mono text-xs text-secondary-text">
                                    <span className="bg-white border border-gray-lines rounded px-2 py-0.5">{from}</span>
                                    <span className="text-route-green font-bold">→</span>
                                    <span className="bg-white border border-gray-lines rounded px-2 py-0.5">{String(to)}</span>
                                </div>
                            ))}
                            {mapEntries.length > 4 && (
                                <span className="text-xs text-secondary-text">{t("+ {{count}} more…", { count: mapEntries.length - 4 })}</span>
                            )}
                        </div>
                    )}
                    {rule.mapping.default !== undefined && (
                        <div className="font-mono text-xs text-secondary-text mt-1">{t("default")}: {String(rule.mapping.default)}</div>
                    )}
                </div>
            </>
        );
    };

    // ---- how to connect (provider API key + endpoint) -----------------------
    // Push/webhook providers are configured by POSTing to Gundi, so their panel
    // leads with the endpoint + API key that used to live on the Provider tab.
    // Draft of the selected account's General fields (name / workspace /
    // endpoint); null means "not edited". Renaming a Connection here never
    // touches the Route's own name — they are decoupled. The draft is dropped
    // when the selection moves: guardedSelect can't catch a selection driven by
    // the URL (browser back/forward, a pasted deep link), and a draft left
    // behind would be saved onto whichever account is open next.
    type GeneralDraft = { name?: string; owner?: { id: string; name: string }; base_url?: string };
    const [generalDraft, setGeneralDraft] = useState<GeneralDraft | null>(null);
    const [savingGeneral, setSavingGeneral] = useState(false);
    useEffect(() => { setGeneralDraft(null); }, [selectedAccountId]);

    // Everything the General pane needs to know about an account's address.
    // EarthRanger accounts edit a site name rather than a URL (<site>.pamdas.org);
    // a site whose stored URL isn't a pamdas one is edited as a plain URL —
    // rewriting it through the site input would silently move it to another host.
    const baseUrlStateFor = (bundle: AccountBundle) => {
        const policy = baseUrlPolicy({
            isProvider: bundle.isProvider,
            techName: bundle.techName,
            techValue: bundle.techValue || routeProvider?.type?.value,
            actions: bundle.actions,
        });
        const erSiteName = isEarthRangerDestinationType(bundle.techName)
            ? extractEarthRangerSiteName(bundle.base_url || "") : "";
        const asSiteName = isEarthRangerDestinationType(bundle.techName)
            && (!bundle.base_url || !!erSiteName);
        return {
            ...policy,
            asSiteName,
            value: asSiteName ? erSiteName : (bundle.base_url ?? ""),
        };
    };
    const selectedBundle = selectedAccountId ? bundles[selectedAccountId] : undefined;
    const selectedBaseUrl = selectedBundle ? baseUrlStateFor(selectedBundle) : undefined;

    // General pane drafts fall back to the stored values field by field.
    const gName = generalDraft?.name ?? selectedBundle?.name ?? "";
    const gOwner = generalDraft?.owner ?? selectedBundle?.owner;
    const gUrl = generalDraft?.base_url ?? selectedBaseUrl?.value ?? "";
    const gNameError = generalDraft !== null && gName.trim().length === 0 ? t("Name is required.") : undefined;
    // Same shared rule every other address field uses.
    const gUrlError = !selectedBaseUrl?.show ? undefined : validateBaseUrl({
        value: gUrl, asSiteName: selectedBaseUrl.asSiteName, optional: selectedBaseUrl.optional, t,
    });
    const generalDirty = generalDraft !== null && !!selectedBundle && (
        gName.trim() !== (selectedBundle.name ?? "")
        || (!!gOwner?.id && gOwner.id !== selectedBundle.owner?.id)
        || gUrl.trim() !== (selectedBaseUrl?.value ?? ""));

    // Resolves true when nothing about the General pane is left unsaved —
    // including when there was never a draft to begin with. Lifted out of the
    // panel so the navigation guards can save this draft too.
    const saveGeneralDraft = async (): Promise<boolean> => {
        if (generalDraft === null || !selectedBundle || !selectedBaseUrl) return true;
        if (!generalDirty) { setGeneralDraft(null); return true; }
        if (gNameError || gUrlError) return false;
        setSavingGeneral(true);
        try {
            const patch: any = {};
            if (gName.trim() !== (selectedBundle.name ?? "")) patch.name = gName.trim();
            if (gOwner?.id && gOwner.id !== selectedBundle.owner?.id) patch.owner = gOwner.id;
            if (gUrl.trim() !== (selectedBaseUrl.value ?? "")) {
                patch.base_url = selectedBaseUrl.asSiteName ? constructEarthRangerUrl(gUrl.trim()) : gUrl.trim();
            }
            await updateConnection(token, selectedBundle.id, patch);
            toast.success(t("Connection updated"));
            setGeneralDraft(null);
            await reload(selectedBundle.id);
            // Lists and headers elsewhere show these fields too.
            props.onReloadRoute?.();
            return true;
        } catch (err: any) {
            console.error("failed to update connection", err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."));
            }
            return false;
        } finally { setSavingGeneral(false); }
    };

    // Everything the user has stashed anywhere in this tab. The navigation guards
    // are leaving the whole selection behind, so they commit all of it rather than
    // just the section on screen.
    const savePendingEdits = async (): Promise<boolean> => {
        if (!(await saveGeneralDraft())) return false;
        const targets = Object.keys(editor.dirtyIds)
            .filter((id) => editor.dirtyIds[id])
            .map((id) => bundles[id])
            .filter(Boolean);
        for (const target of targets) {
            if (!(await editor.save(target))) return false;
        }
        return true;
    };
    const discardPendingEdits = () => { editor.discardAll(); setGeneralDraft(null); };
    // API keys for destinations that send data back. The primary provider's key
    // arrives via props; these are fetched lazily when such a panel opens.
    const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
    // Requested ids, not just resolved ones: `selection` is rebuilt every render
    // and `arrows` changes identity on every structural rebuild, so keying only
    // off `apiKeys` fired a second request for every render while the first was
    // still in flight. Same ref-guard as sharedFetched above.
    const apiKeyFetched = useRef<Set<string>>(new Set());
    // Which accounts have a key worth asking for: the provider always, and a
    // destination once it sends data back.
    const selectedNeedsKey = selectedAccountId
        ? selectedAccountId === providerId || arrows.some((a) => a.destinationId === selectedAccountId
            && a.direction === "in" && a.state === "enabled")
        : false;
    // Lazy, and only for the CONNECT section: every other section of the panel
    // opens without spending a key request (the provider's used to be fetched on
    // every Route page load, whatever tab the user was on).
    const openAccountSection = selection.kind === "account" ? selection.section : undefined;
    useEffect(() => {
        if (!token || !selectedAccountId || openAccountSection !== "how-to-connect") return;
        if (!selectedNeedsKey || apiKeyFetched.current.has(selectedAccountId)) return;
        const id = selectedAccountId;
        apiKeyFetched.current.add(id);
        // No cancelled-guard: the write is keyed by id, so storing a late response
        // is always safe — and skipping it while the id stayed latched left the
        // section on "Loading…" for the rest of the page's life.
        getRouteApiKey(token, id)
            .then((r: any) => {
                if (r?.api_key) {
                    setApiKeys((prev) => ({ ...prev, [id]: r.api_key }));
                    return;
                }
                // No key and no throw: a swallowed 401 resolves undefined. Un-mark
                // it so reopening the section after logging back in tries again,
                // instead of showing "Loading…" until a full reload.
                apiKeyFetched.current.delete(id);
            })
            .catch((e: any) => {
                apiKeyFetched.current.delete(id);
                console.error("failed to load API key for account", id, e);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedAccountId, openAccountSection, selectedNeedsKey]);



    const accountPanel = () => {
        if (selection.kind !== "account") return null;
        const bundle = bundles[selection.integrationId];
        if (!bundle) {
            return (
                <div className="border border-gray-lines rounded-lg bg-white p-5">
                    {[0, 1, 2].map((i) => <div key={i} className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse mb-3" />)}
                </div>
            );
        }
        const dest = destinations.find((d: any) => d.id === bundle.id);

        // Every action the account owns is configured here, grouped by what it
        // does: credentials, then the directions data can move (pull = this
        // system sends, push = it receives), with anything else under Advanced
        // Config at the bottom. The account's name heads the list — a Connection
        // can be renamed from either side of the map.
        const withFields = bundle.actions.filter(isActionVisible);
        const allActions = withFields;
        // One General pane carries the account's name, workspace and endpoint.
        // Whether the endpoint shows, and whether it may stay empty, is the shared
        // base-URL policy's call.
        const urlState = bundle.id === selectedBundle?.id && selectedBaseUrl
            ? selectedBaseUrl : baseUrlStateFor(bundle);
        // The action grouping is the shared one, so this panel and the Connection
        // page can't drift apart (a hand-rolled copy here put an auth action typed
        // `pull` in both Credentials and Send data).
        const nav: NavSection[] = buildActionNav({
            target: bundle,
            actions: withFields,
            t,
            detailsLabel: t("General"),
            detailsSlug: "general",
            detailsNeedsSetup: urlState.show && !urlState.optional && !bundle.base_url,
        });
        // A destination that sends data back is a data provider too — its
        // developers need its own API key, not just the primary provider's.
        const sendsBack = !bundle.isProvider && arrows.some((a) =>
            a.destinationId === bundle.id && a.direction === "in" && a.state === "enabled");
        const providerType = bundle.isProvider ? routeProvider?.type : undefined;
        // Webhook providers keep the webhook endpoint + headers; everything else
        // gets the sensors endpoint. A type that carries a webhook is a webhook
        // here even when it also has pull config (kind would say "standard").
        const routeKind = providerType && (getRouteKind(providerType) === "webhook" || providerType.webhook)
            ? "webhook" : "api-push";
        // Webhook payload parsing is provider-side config, so it lives on this
        // panel (it used to be its own tab).
        const webhookConfig = bundle.isProvider && (routeProvider?.webhook_configuration || routeProvider?.type?.webhook)
            ? (routeProvider.webhook_configuration ?? {
                id: "", integration: routeProvider.id,
                webhook: routeProvider.type?.webhook ?? { id: "", name: "", value: "" },
                data: null,
            })
            : undefined;
        // Insert ahead of the catch-all group so Advanced Config stays last.
        // Re-found per insert: splicing shifts the catch-all group's index, and a
        // cached one put the second insert ahead of the first (CONNECT before
        // WEBHOOK) instead of both before Advanced Config.
        const insertBeforeAdvanced = (section: NavSection) => {
            const advancedAt = nav.findIndex((s) => s.cat === t(ADVANCED_CAT));
            if (advancedAt === -1) nav.push(section);
            else nav.splice(advancedAt, 0, section);
        };
        if (webhookConfig) {
            insertBeforeAdvanced({
                cat: t("WEBHOOK"),
                items: [{ slug: "webhook", label: t("Payload parsing"), needsSetup: !routeProvider?.webhook_configuration?.data }],
            });
        }
        // Last section, and present on every account that acts as a data
        // provider: the primary provider always, and a destination once it sends
        // data back — both sides of a bidirectional Route have their own API key.
        if (providerType || sendsBack) {
            insertBeforeAdvanced({ cat: t("CONNECT"), items: [{ slug: "how-to-connect", label: t("API key") }] });
        }

        // Old bookmarks may still address the split sections, and a section the
        // URL names but this account doesn't offer would render an empty panel —
        // fall back to the first section either way.
        const requestedSlug = selection.section === "name" || selection.section === "endpoint"
            ? "general" : selection.section;
        const navSlugs = nav.flatMap((section) => section.items.map((item) => item.slug));
        const activeSlug = (requestedSlug && navSlugs.includes(requestedSlug) ? requestedSlug : undefined)
            || nav[0]?.items[0]?.slug;
        const activeAction = allActions.find((a) => slugOf(a) === activeSlug);
        // Health dot colors: green = healthy, red = unhealthy, gray = every
        // other status (disabled, needs_review, unknown…). Gray makes no claim
        // about data flow — a needs_review account may well be sending — it
        // only promises the health lookup did not say "unhealthy".
        const statusKey = String(bundle.status || "").toLowerCase();
        const statusDotColor = statusKey === "healthy" ? "bg-route-green"
            : statusKey === "unhealthy" ? "bg-remove-red"
            : "bg-medium-gray";
        // Saving is per section, so Save/Cancel act on the active section only —
        // the section-switch prompt guarantees no other section is dirty.
        const isDirty = !!activeAction && editor.isActionDirty(bundle.id, activeAction.id);
        // An action that was never saved, or that is still missing required
        // fields, can be committed with the values on screen, so Save is live
        // even before the user pokes a field.
        const canCommitDefaults = !!activeAction && actionCanCommitForm(bundle, activeAction);

        // Section switches inside the account ask the Save-or-discard question
        // whenever the section being left has unsaved work.
        const goToSection = (slug: string) => select({ kind: "account", integrationId: bundle.id, section: slug });
        const selectSection = (slug: string) => {
            if (slug === activeSlug) return;
            if (activeSlug === "general" && generalDirty) {
                return setSectionPrompt({
                    onSave: saveGeneralDraft,
                    onDiscard: () => setGeneralDraft(null),
                    proceed: () => goToSection(slug),
                });
            }
            if (activeAction && editor.isActionDirty(bundle.id, activeAction.id)) {
                return setSectionPrompt({
                    onSave: () => editor.save(bundle, activeAction.id),
                    onDiscard: () => editor.cancel(bundle, activeAction.id),
                    proceed: () => goToSection(slug),
                });
            }
            goToSection(slug);
        };
        // EarthRanger only: the ER account (username or truncated token) this
        // destination authenticates with — the bundle already has the configs.
        const erAccount = dest ? getDestinationAccount({ ...dest, configurations: bundle.configurations } as any) : null;

        return (
            <SelectionPanel
                title={bundle.name}
                subtitle={<span className="flex items-center gap-2">
                    {bundle.techName && <span className="bg-light-green text-route-green rounded-full px-2 py-0.5 text-xs font-semibold">{bundle.techName}</span>}
                    {bundle.base_url && <span>· {bundle.base_url}</span>}
                    {erAccount && <span className="font-mono">· {erAccount}</span>}
                    <HoverTooltip content={bundle.status || t("unknown")} position="top" portal focusable={false}>
                        <span className={`block w-2 h-2 rounded-full ${statusDotColor}`} />
                    </HoverTooltip>
                </span>}
                headerActions={<>
                    {/* The account's own screen: every Route using it, and the
                        record itself outside this Route's context. Routed through
                        the same Save-or-discard prompt as a selection change. */}
                    <button type="button"
                        onClick={() => guardedNavigate(`/connections/${bundle.id}/configuration`)}
                        className={`${buttonClasses.inlineLink} self-center`}>
                        {t("View Connection")} ▸
                    </button>
                    {!bundle.isProvider && dest && (
                        <button className={buttonClasses.lightRed}
                            onClick={() => setConfirmation({
                                title: t("Remove from Route"),
                                body: t("Remove {{name}} from this Route? The site itself is not deleted — it just stops receiving data from this flow.", { name: bundle.name }),
                                confirmLabel: t("Remove"),
                                destructive: true,
                                onConfirm: () => { Promise.resolve(props.onRemoveSite(dest)).then(() => select({ kind: "none" })); },
                            })}>
                            {t("Remove from Route")}
                        </button>
                    )}
                </>}
                banner={<SharedAccountNotice
                    accountName={bundle.name}
                    usedBy={bundle.isProvider ? (props.providerSharedWith || []) : (sharedBySite[bundle.id] || [])} />}
                nav={nav}
                activeSlug={activeSlug}
                onSelectSection={selectSection}
                emptyNavNote={t("This account has no general configuration.")}
                footer={activeSlug === "general" ? <>
                    <button className={buttonClasses.primary}
                        disabled={!generalDirty || !!gNameError || !!gUrlError || savingGeneral}
                        onClick={saveGeneralDraft}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={generalDraft === null}
                        onClick={() => setGeneralDraft(null)}>{t("Cancel")}</button>
                </> : activeAction ? <>
                    <button className={buttonClasses.primary} disabled={!isDirty && !canCommitDefaults}
                        onClick={() => { editor.commitDefaults(bundle, activeAction); editor.save(bundle, activeAction.id); }}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={!isDirty} onClick={() => editor.cancel(bundle, activeAction.id)}>{t("Cancel")}</button>
                </> : undefined}>
                {activeSlug === "general"
                    ? <div className="rounded border border-field-outline bg-white p-4">
                        <div className="mb-2 border-b border-divider-lines pb-3 flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                            <div className="md:w-1/2 flex flex-col">
                                <label htmlFor="account_name" className="mr-1">{t("Name")} *</label>
                                <span className="text-sm text-secondary-gray mt-1">{t("A descriptive name for this Connection.")}</span>
                            </div>
                            <div className="md:w-1/2">
                                <input
                                    id="account_name"
                                    data-testid="ConfigurationsTab-account-name-input"
                                    value={gName}
                                    onChange={(e) => setGeneralDraft((prev) => ({ ...prev, name: e.target.value }))}
                                    className={`w-full p-1.5 border rounded px-2 transition-colors duration-300 ${
                                        gNameError ? "border-orange" : "border-neutral-gray-200"}`}
                                />
                                {gNameError && <p className="text-orange-600 text-sm mt-1">{gNameError}</p>}
                            </div>
                        </div>
                        <div className="mb-2 border-b border-divider-lines pb-3 flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                            <div className="md:w-1/2 flex flex-col">
                                <label htmlFor="account_workspace" className="mr-1">{t("Workspace")} *</label>
                                <span className="text-sm text-secondary-gray mt-1">{t("The workspace this Connection belongs to.")}</span>
                            </div>
                            <div className="md:w-1/2">
                                <SelectInput
                                    inputId="account_workspace"
                                    options={(props.organizations || []).map((org: any) => ({ label: org.name, value: org.id }))}
                                    className={"basic-single"}
                                    value={gOwner?.id ? { label: gOwner.name, value: gOwner.id } : null}
                                    isDisabled={false}
                                    isLoading={false}
                                    isClearable={false}
                                    isSearchable={true}
                                    isMulti={false}
                                    name={t("Workspace")}
                                    onChange={(e: any) => setGeneralDraft((prev) => ({ ...prev, owner: { id: e.value, name: e.label } }))}
                                    onMenuScrollToBottom={props.onLoadMoreOrgs}
                                    handleInputChange={props.onSearchOrgs}
                                />
                            </div>
                        </div>
                        {urlState.show && (
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                            <div className="md:w-1/2 flex flex-col">
                                <label htmlFor="account_base_url" className="mr-1">{urlState.asSiteName ? t("Site URL") : t("URL")}{urlState.optional ? "" : " *"}</label>
                                <span className="text-sm text-secondary-gray mt-1">
                                    {urlState.asSiteName
                                        ? t("The name of the EarthRanger site (e.g. my-site).")
                                        : urlState.derived
                                            ? t("This technology derives its address from its configuration. Set a URL only if you need to override it.")
                                            : t("The address Gundi uses to reach this system.")}
                                </span>
                            </div>
                            <div className="md:w-1/2">
                                {urlState.asSiteName
                                    ? <EarthRangerSiteInput
                                        value={gUrl}
                                        onChange={(e: any) => setGeneralDraft((prev) => ({ ...prev, base_url: e.target.value }))}
                                        error={gUrlError}
                                      />
                                    : <>
                                        <input
                                            id="account_base_url"
                                            value={gUrl}
                                            onChange={(e) => setGeneralDraft((prev) => ({ ...prev, base_url: e.target.value }))}
                                            className={`w-full p-1.5 border rounded px-2 transition-colors duration-300 ${
                                                gUrlError ? "border-orange" : "border-neutral-gray-200"}`}
                                        />
                                        {gUrlError && <p className="text-orange-600 text-sm mt-1">{gUrlError}</p>}
                                      </>}
                            </div>
                        </div>
                        )}
                      </div>
                    : activeSlug === "how-to-connect"
                    ? <HowToConnect
                        kind={bundle.isProvider ? routeKind : "api-push"}
                        integrationType={(bundle.isProvider ? routeProvider?.type?.value : dest?.type?.value) || ""}
                        apiKey={apiKeys[bundle.id]}
                        isAdmin={currentMemberIsAdmin} />
                    : activeSlug === "webhook" && webhookConfig
                        ? <RouteDetailsWebhook
                            webhookConfiguration={webhookConfig}
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            onSave={props.onSaveWebhook}
                            onCancel={props.onCancelWebhook}
                            changesMade={props.webhookChangesMade}
                            onChangesMade={props.onWebhookChangesMade}
                          />
                        : activeAction
                            ? renderActionForm(bundle, activeAction, true)
                            : <p className="text-secondary-text text-sm">{t("This account has no general configuration.")}</p>}
            </SelectionPanel>
        );
    };

    const flowPanel = () => {
        if (selection.kind !== "flow") return null;
        const arrow = arrows.find((a) => a.id === `${selection.destinationId}-${selection.direction}`);
        const providerBundle = bundles[providerId];
        const destBundle = bundles[selection.destinationId];
        const dest = destinations.find((d: any) => d.id === selection.destinationId);
        if (!arrow || !providerBundle || !destBundle) {
            return (
                <div className="border border-gray-lines rounded-lg bg-white p-5">
                    {[0, 1, 2].map((i) => <div key={i} className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse mb-3" />)}
                </div>
            );
        }

        const sender = arrow.direction === "out" ? providerBundle : destBundle;
        const receiver = arrow.direction === "out" ? destBundle : providerBundle;
        // Pull/push settings are configured on the accounts that own them; the
        // arrow carries what belongs to the flow itself — its on/off state and,
        // for the outgoing direction, the field-mapping rules.
        const nav: NavSection[] = arrow.direction === "out"
            ? [{ cat: t("RULES"), items: [{ slug: "field-mapping", label: t("Field mapping") }] }]
            : [];

        const activeSlug = selection.section || nav[0]?.items[0]?.slug;

        const headerActions = arrow.direction === "out" ? (
            // The whole Route's on/off switch lives here — same gesture as
            // disabling a reverse flow, one arrow to the right of it.
            routeEnabled ? (
                <span className="flex items-center gap-2">
                    <span className="text-xs text-secondary-text">{t("Sending")}</span>
                    <CustomCheckbox label={t("Sending")} formData={true} onChange={() => setConfirmation({
                        title: t("Stop sending data"),
                        // `enabled` is a field on the provider Connection, not on the
                        // route record, so this stops every Route that Connection
                        // feeds — don't claim it only affects this one.
                        // null = the "which other Routes" lookup failed, so the
                        // reassuring single-Route wording would be a guess.
                        body: props.providerAlsoProvidesFor === null
                            ? t("Stop sending data from {{name}}? This Connection may provide data to other Routes, which would stop too. The configuration itself is kept.",
                                { name: providerBundle.name })
                            : (props.providerAlsoProvidesFor ?? 0) > 0
                            ? t("Stop sending data from {{name}}? It also provides data to {{count}} other Route, which stops too. The configuration itself is kept.",
                                { name: providerBundle.name, count: props.providerAlsoProvidesFor })
                            : t("Stop sending data from {{name}}? Data stops flowing to every destination of this Route. The configuration itself is kept.",
                                { name: providerBundle.name }),
                        confirmLabel: t("Stop sending"),
                        destructive: true,
                        onConfirm: () => props.onToggleRouteEnabled?.(false),
                    })} />
                </span>
            ) : (
                <span className="flex items-center gap-2">
                    <span className="text-xs text-secondary-text">{t("Enable")}</span>
                    <CustomCheckbox label={t("Enable")} formData={false}
                        onChange={() => props.onToggleRouteEnabled?.(true)} />
                </span>
            )
        ) : arrow.state === "enabled" ? (
            <span className="flex items-center gap-2">
                <span className="text-xs text-secondary-text">{t("Sending")}</span>
                <CustomCheckbox label={t("Sending")} formData={true} onChange={() => dest && setConfirmation({
                    title: t("Stop sending data"),
                    body: t("Stop sending data from {{from}} to {{to}}? This removes the reverse Route; the configuration itself is kept.", { from: destBundle.name, to: providerBundle.name }),
                    confirmLabel: t("Stop sending"),
                    destructive: true,
                    onConfirm: () => { Promise.resolve(props.onDisableReverseFlow(dest)).then(() => props.onReloadRoute()); },
                })} />
            </span>
        ) : arrow.state === "disabled" ? (
            <span className="flex items-center gap-2">
                <span className="text-xs text-secondary-text">{t("Enable")}</span>
                <CustomCheckbox label={t("Enable")} formData={false} onChange={() => {
                    if (dest) Promise.resolve(props.onEnableReverseFlow(dest)).then(() => props.onReloadRoute());
                }} />
            </span>
        ) : undefined;

        const note = arrow.direction === "out" && !routeEnabled
            ? t("This Route is disabled. Use the toggle above to resume sending.")
            : arrow.direction === "in" && arrow.state === "disabled"
            ? t("Not enabled yet. Configure both accounts on the map, then turn this direction on.")
            : undefined;

        return (
            <SelectionPanel
                title={`${sender.name} → ${receiver.name}`}
                subtitle={note ? <span className="text-xs text-secondary-text">{note}</span> : undefined}
                headerActions={headerActions}
                banner={arrow.state === "not-enableable" && arrow.reason
                    // Same amber treatment as SharedAccountNotice — it's a warning, not a neutral note.
                    ? <div className="mb-5 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black">{arrow.reason}</div>
                    : undefined}
                nav={nav}
                activeSlug={activeSlug}
                onSelectSection={(slug) => select({ kind: "flow", destinationId: selection.destinationId, direction: selection.direction, section: slug })}
                emptyNavNote={t("Nothing to configure for this direction.")}>
                {activeSlug === "field-mapping"
                    ? fieldMappingSection(dest)
                    : <p className="text-secondary-text text-sm">
                        {t("Pull and push settings are configured on the accounts — click a node on the map.")}
                      </p>}
            </SelectionPanel>
        );
    };

    return (
        <div className="mt-8 px-4 pb-28">
            {failed && (
                <div className="mb-4 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black flex flex-wrap items-center gap-3">
                    <span>{t("Some accounts in this Route couldn't be loaded, so parts of the map may be incomplete.")}</span>
                    <button type="button" className={`${buttonClasses.lightGreen} ml-auto`} onClick={() => reload()}>
                        {t("Retry")}
                    </button>
                </div>
            )}
            {destinations.length === 0 && (
                <p className="text-secondary-text text-sm mb-3">{t("No destinations yet. Use the “Add destination” button to connect one.")}</p>
            )}
            {selection.kind === "none" && (
                <p className="text-secondary-text text-sm mb-3">
                    {loading ? t("Loading accounts…") : t("Select an account or a flow below to configure it.")}
                </p>
            )}
            <RouteFlowMap
                nodes={nodes}
                edges={edges}
                destinationCount={destinations.length}
                routeStatus={route?.status}
                routeEnabled={routeProvider?.enabled !== false}
                onSelectAccount={(id: string) => guardedSelect({ kind: "account", integrationId: id })}
                onSelectFlow={(destinationId: string, direction: FlowDirectionKey) => guardedSelect({ kind: "flow", destinationId, direction })}
                onAddDestination={props.onAddDestination}
                onEnableRoute={() => props.onToggleRouteEnabled?.(true)}
                highlightAddDestination={!!props.highlightAddDestination}
            />
            {confirmation && (
                <ModalShell
                    title={confirmation.title}
                    onClose={() => setConfirmation(null)}
                    footer={<>
                        <button type="button"
                            className={confirmation.destructive ? buttonClasses.destructive : buttonClasses.primary}
                            onClick={() => { const run = confirmation.onConfirm; setConfirmation(null); run(); }}>
                            {confirmation.confirmLabel}
                        </button>
                        <button type="button" className={buttonClasses.cancel} onClick={() => setConfirmation(null)}>
                            {t("Cancel")}
                        </button>
                    </>}>
                    <p className="text-base text-off-black">{confirmation.body}</p>
                </ModalShell>
            )}
            <UnsavedSectionPrompt prompt={sectionPrompt} onClose={() => setSectionPrompt(null)} />
            {selection.kind === "account" && accountPanel()}
            {selection.kind === "flow" && flowPanel()}
        </div>
    );
};

export default ConfigurationsTab;
